import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip } from '@material-ui/core'
import { PageWrapper, ViewWrapper, ViewSpacerWrapper, ActionButton, ConditionalRender } from 'components/widgets'
import { RenderCounter } from 'components/widgets/RenderCounter'
import { ALL_ORGS_SELECTED, PRIMARY_COLOR } from 'lib/constants'
import { useGlobal } from 'contexts/GlobalProvider'

import VoiceCards, { VoiceGroupViewModel } from './VoiceCards'
import VoiceDevicesModal from './VoiceDevicesModal/VoiceDevicesModal'
import AddDeviceGroupModal, { AddDeviceGroupResult } from './AddDeviceGroupModal'

import { useChannels } from 'contexts/ChannelsProvider'

import AddChannelModal from 'components/partials/AddChannelModal'
import { createChannel as apiCreateChannel, deleteChannel as apiDeleteChannel } from 'models/api/channels'
import {
  deleteDeviceGroup as apiDeleteDeviceGroup,
  addAlertSourceToDeviceGroup,
  upsertDeviceGroup,
} from 'models/api/deviceGroups'

import type { Channel } from 'lib/types/channels'
import { isAdmin } from 'lib/utils/auth'
import { LANGUAGE_LABEL_BY_CODE } from 'lib/types/languages'

function playTestSound() {
  try {
    const synth = window.speechSynthesis
    if (!synth) return
    synth.cancel()
    const utter = new SpeechSynthesisUtterance('Testing')
    utter.rate = 1
    utter.pitch = 1
    utter.volume = 1
    synth.speak(utter)
  } catch (e) {
    console.error('Failed to play test sound:', e)
  }
}

const DEFAULT_LANGUAGE = 'en'

function normalizeLanguage(code?: string): string {
  return String(code || '').trim() || DEFAULT_LANGUAGE
}

// Groups are now identified by their language, so the group name is derived
// from the selected language rather than entered by hand.
function languageName(code: string): string {
  return LANGUAGE_LABEL_BY_CODE[code] || code
}

const DEFAULT_GROUP_NAME = 'Default'

// Names must be unique, so scope them to the channel: "<channel> - <language>",
// or "<channel> - Default" for Elite groups with no language. Prevents the 422
// you'd otherwise get from two channels both creating e.g. an "English" group.
function buildGroupName(channelName: string, suffix: string): string {
  return `${String(channelName || '').trim()} - ${suffix}`
}

function VoicePage() {
  const { selectedOrgId, selectedOrg } = useGlobal()

  const {
    channels,
    deviceGroups,
    upsertDeviceGroupOptimistic,
    refreshDeviceGroups,
    refreshChannels,
    setChannels,
    loading,
  } = useChannels()

  const [devicesOpen, setDevicesOpen] = useState(false)
  const [devicesGroup, setDevicesGroup] = useState<VoiceGroupViewModel | null>(null)

  function openDevices(group: VoiceGroupViewModel) {
    setDevicesGroup(group)
    setDevicesOpen(true)
  }

  function closeDevices() {
    setDevicesOpen(false)
    setDevicesGroup(null)
  }

  const groups: VoiceGroupViewModel[] = useMemo(() => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return []

    const channelByName = new Map<string, Channel>()
    for (const c of channels || []) {
      const name = String((c as any)?.name ?? '').trim()
      if (name) channelByName.set(name, c)
    }

    return (deviceGroups || [])
      .filter(dg => String((dg as any).orgId || '') === String(selectedOrgId))
      .map(dg => {
        const channelName = String((dg as any).channelName || '').trim()

        const ch =
          (channelName ? channelByName.get(channelName) : undefined) ||
          ({
            id: channelName || `unknown:${String((dg as any).id)}`,
            name: channelName || 'Unknown Channel',
            orgId: selectedOrgId,
            health: 2,
          } as any as Channel)

        return {
          id: String((dg as any).id),
          baseChannelId: String((ch as any)?.id ?? (ch as any)?.name ?? channelName),
          baseChannelName: String((ch as any)?.name ?? channelName),
          language: normalizeLanguage((dg as any).language),
          deviceGroupName: String((dg as any).name || ''),
          linkedChannelName: String((ch as any)?.name ?? channelName),
          deviceCount: Array.isArray((dg as any).alertSources) ? (dg as any).alertSources.length : 0,
          channel: ch,
          color: String((dg as any).color || ''),
          wifiNetId: String((dg as any).wifiNetId || ''),
        }
      })
      .filter(g => Boolean(String(g.baseChannelName || '').trim()))
  }, [channels, deviceGroups, selectedOrgId])

  // After a downgrade from Ultimate to Elite, channels can be left with several
  // per-language device groups. Elite supports only one group per channel, so
  // collapse each channel's groups into one: keep the fullest, move the other
  // groups' devices into it, delete the emptied groups, and reset the survivor
  // to a single default (no-language) group. Runs once per leftover dataset.
  const collapsingRef = useRef(false)
  useEffect(() => {
    if (collapsingRef.current) return
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return
    // Only collapse for a CONFIRMED Elite org. Anything else — Ultimate (which
    // legitimately keeps per-language groups), or a not-yet-loaded org whose
    // tier is still unknown — must be left alone, otherwise a transient null
    // tier would merge an Ultimate org's groups and undo device moves.
    const tier = String((selectedOrg as any)?.subscriptionTier || '').toLowerCase()
    if (tier !== 'elite') return

    const byChannel = new Map<string, any[]>()
    for (const dg of deviceGroups || []) {
      if (String((dg as any).orgId || '') !== String(selectedOrgId)) continue
      const ch = String((dg as any).channelName || '').trim()
      if (!ch) continue
      const list = byChannel.get(ch) || []
      list.push(dg)
      byChannel.set(ch, list)
    }

    const channelsToCollapse = Array.from(byChannel.entries()).filter(([, list]) => list.length > 1)
    if (channelsToCollapse.length === 0) return

    collapsingRef.current = true
    ;(async () => {
      try {
        for (const [channelName, list] of channelsToCollapse) {
          // Keep the group with the most devices; merge the rest into it.
          const sorted = [...list].sort(
            (a, b) => (b.alertSources?.length || 0) - (a.alertSources?.length || 0),
          )
          const primary = sorted[0]
          const others = sorted.slice(1)

          for (const g of others) {
            try {
              for (const as of g.alertSources || []) {
                if (as?.id) await addAlertSourceToDeviceGroup(String(primary.id), String(as.id), selectedOrgId)
              }
              // Only delete once its devices have been safely re-homed.
              await apiDeleteDeviceGroup(String(g.id), selectedOrgId)
            } catch (e) {
              console.error('Failed to collapse device group:', e)
            }
          }

          // Reset the survivor to a single default group with no language.
          try {
            await upsertDeviceGroup({
              id: String(primary.id),
              orgId: selectedOrgId,
              name: buildGroupName(channelName, DEFAULT_GROUP_NAME),
              channelName,
              language: DEFAULT_LANGUAGE,
              color: primary.color || undefined,
            })
          } catch (e) {
            console.error('Failed to normalize collapsed device group:', e)
          }
        }
      } finally {
        await refreshDeviceGroups()
        collapsingRef.current = false
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceGroups, selectedOrgId, selectedOrg])

  const [addOpen, setAddOpen] = useState(false)
  const [addBusy, setAddBusy] = useState(false)

  function handleAddChannel() {
    setAddOpen(true)
  }

  async function handleCreateChannel(name: string) {
    const trimmed = (name || '').trim()
    if (!trimmed) return

    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }

    setAddBusy(true)
    try {
      const created = await apiCreateChannel(trimmed, selectedOrgId)

      if (created) {
        setChannels(prev => {
          const list = Array.isArray(prev) ? prev : []
          const exists = list.some(c => c.id === (created as any).id || c.name === (created as any).name)
          return exists
            ? list
            : ([{ id: trimmed, name: trimmed, orgId: selectedOrgId, health: 2 } as any as Channel, ...list] as any)
        })
      }

      // Elite (non-Ultimate) orgs don't manage language variants, so every new
      // channel gets a single default device group automatically.
      const isUltimate = String((selectedOrg as any)?.subscriptionTier || '').toLowerCase() === 'ultimate'
      if (!isUltimate) {
        try {
          await upsertDeviceGroupOptimistic({
            orgId: selectedOrgId,
            name: buildGroupName(trimmed, DEFAULT_GROUP_NAME),
            channelName: trimmed,
            language: DEFAULT_LANGUAGE,
            color: undefined,
          })
        } catch (e) {
          console.warn('Failed to create default device group (non-fatal):', e)
        }
      }

      await refreshChannels()
      await refreshDeviceGroups()
      setAddOpen(false)
    } catch (err) {
      console.error('Error creating channel:', err)
      alert('Failed to create channel. Check console.')
    } finally {
      setAddBusy(false)
    }
  }

  const [addGroupOpen, setAddGroupOpen] = useState(false)
  const [addGroupBusy, setAddGroupBusy] = useState(false)
  const [addGroupChannel, setAddGroupChannel] = useState<Channel | null>(null)

  function openAddGroup(channel: Channel) {
    setAddGroupChannel(channel)
    setAddGroupOpen(true)
  }

  function closeAddGroup() {
    if (addGroupBusy) return
    setAddGroupOpen(false)
    setAddGroupChannel(null)
  }

  async function handleSubmitAddGroup(result: AddDeviceGroupResult) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }

    const channelName = String((addGroupChannel as any)?.name ?? '').trim()
    if (!channelName) return

    const isUltimate = String((selectedOrg as any)?.subscriptionTier || '').toLowerCase() === 'ultimate'
    const langLower = isUltimate ? normalizeLanguage(result.language) : DEFAULT_LANGUAGE
    const groupName = buildGroupName(channelName, languageName(langLower))

    // A channel can hold one group per language; re-adding a language updates
    // the existing group (e.g. its color) rather than creating a duplicate.
    const existing = (deviceGroups || []).find(dg => {
      const sameOrg = String((dg as any).orgId || '') === String(selectedOrgId)
      const sameChannel = String((dg as any).channelName || '').trim() === channelName
      const sameLanguage = normalizeLanguage((dg as any).language) === langLower
      return sameOrg && sameChannel && sameLanguage
    })

    const payload: any = {
      ...(existing?.id ? { id: String((existing as any).id) } : {}),
      orgId: selectedOrgId,
      name: groupName,
      channelName,
      language: langLower,
      color: result.color || undefined,
      wifiNetId: String((existing as any)?.wifiNetId || ''),
    }

    setAddGroupBusy(true)
    try {
      await upsertDeviceGroupOptimistic(payload)
      await refreshDeviceGroups()
      closeAddGroup()
    } catch (e) {
      console.error('Failed to add group:', e)
      alert('Failed to add group. Check console.')
      await refreshDeviceGroups()
    } finally {
      setAddGroupBusy(false)
    }
  }

  async function handleColorChange(group: VoiceGroupViewModel, hex: string | null) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return

    try {
      await upsertDeviceGroupOptimistic({
        id: String(group.id),
        orgId: selectedOrgId,
        name: group.deviceGroupName || 'Group',
        channelName: String(group.baseChannelName || ''),
        language: normalizeLanguage(group.language),
        wifiNetId: (group as any).wifiNetId || '',
        // null (not undefined) so clearing to default survives JSON serialization.
        color: hex || null,
      })
      await refreshDeviceGroups()
    } catch (e) {
      console.error('Failed to save color:', e)
      alert('Failed to save color. Check console.')
      await refreshDeviceGroups()
    }
  }

  async function handleLanguageChange(group: VoiceGroupViewModel, language: string) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return

    const isUltimate = String((selectedOrg as any)?.subscriptionTier || '').toLowerCase() === 'ultimate'
    if (!isUltimate) return

    const nextLang = normalizeLanguage(language)
    try {
      await upsertDeviceGroupOptimistic({
        id: String(group.id),
        orgId: selectedOrgId,
        // Keep the group name in sync with its channel + language.
        name: buildGroupName(String(group.baseChannelName || ''), languageName(nextLang)),
        channelName: String(group.baseChannelName || ''),
        language: nextLang,
        wifiNetId: (group as any).wifiNetId || '',
        color: group.color || undefined,
      })
      await refreshDeviceGroups()
    } catch (e) {
      console.error('Failed to save language:', e)
      alert('Failed to save language. Check console.')
      await refreshDeviceGroups()
    }
  }

  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null)
  const [deletingChannelName, setDeletingChannelName] = useState<string | null>(null)

  async function handleDeleteGroup(group: VoiceGroupViewModel) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }
    if ((group?.deviceCount ?? 0) > 0) return

    const id = String(group?.id || '')
    if (!id) return

    setDeletingGroupId(id)
    try {
      await apiDeleteDeviceGroup(id, selectedOrgId)
      await refreshDeviceGroups()
    } catch (e) {
      console.error('Failed to delete device group:', e)
      alert('Failed to delete device group. Check console.')
      await refreshDeviceGroups()
    } finally {
      setDeletingGroupId(null)
    }
  }

  async function handleDeleteChannel(channel: Channel) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }

    const channelName = String((channel as any)?.name ?? '').trim()
    if (!channelName) return

    const hasGroups = (deviceGroups || []).some(dg => {
      const sameOrg = String((dg as any).orgId || '') === String(selectedOrgId)
      const sameChannel = String((dg as any).channelName || '').trim() === channelName
      return sameOrg && sameChannel
    })
    if (hasGroups) return

    const channelId = String((channel as any)?.id ?? channelName).trim()
    if (!channelId) return

    setDeletingChannelName(channelName)
    try {
      await apiDeleteChannel(channelId, channelName, selectedOrgId)

      setChannels(prev => {
        const list = Array.isArray(prev) ? prev : []
        return list.filter(c => String((c as any)?.name ?? '').trim() !== channelName) as any
      })

      await refreshChannels()
    } catch (e) {
      console.error('Failed to delete channel:', e)
      alert('Failed to delete channel. Check console.')
      await refreshChannels()
    } finally {
      setDeletingChannelName(null)
    }
  }

  return (
    <PageWrapper>
      <ViewWrapper>
        <ViewSpacerWrapper>
          <RenderCounter name="VoicePage" />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontFamily: 'Montserrat', fontSize: 24, fontWeight: 800, color: PRIMARY_COLOR }}>
              {'Channels'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title="Make sure your browser/system sound is on and turned up to hear the test."
                arrow
                placement="bottom"
              >
                <span>
                  <ActionButton onClick={playTestSound}>{'Test Sound'}</ActionButton>
                </span>
              </Tooltip>
              <ConditionalRender condition={isAdmin(selectedOrgId)}>
                <ActionButton onClick={handleAddChannel}>{'Add Channel'}</ActionButton>
              </ConditionalRender>
            </div>
          </div>

          <div style={{ marginTop: 12, opacity: loading ? 0.75 : 1 }}>
            <VoiceCards
              channels={channels}
              groups={groups}
              onViewDevices={openDevices}
              onAddGroup={openAddGroup}
              onColorChange={handleColorChange}
              onLanguageChange={handleLanguageChange}
              onDeleteGroup={handleDeleteGroup}
              onDeleteChannel={handleDeleteChannel}
              deletingGroupId={deletingGroupId}
              deletingChannelName={deletingChannelName}
            />
          </div>

          <VoiceDevicesModal open={devicesOpen} group={devicesGroup} onClose={closeDevices} />

          <AddChannelModal
            open={addOpen}
            onClose={() => !addBusy && setAddOpen(false)}
            onSubmit={handleCreateChannel}
            busy={addBusy}
          />

          <AddDeviceGroupModal
            open={addGroupOpen}
            channelName={String((addGroupChannel as any)?.name ?? '').trim() || ''}
            busy={addGroupBusy}
            onClose={closeAddGroup}
            onSubmit={handleSubmitAddGroup}
          />

          <div style={{ height: 700 }} />
        </ViewSpacerWrapper>
      </ViewWrapper>
    </PageWrapper>
  )
}

export default memo(VoicePage)
